import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useCallback, useState } from "react";

// components
import { Icon } from "@trussworks/react-uswds";
import AppliedFilterSentence from "components/AppliedFilterSentence";
import { RoundedButton } from "components/LibraryComponents/Components";
import ChooseDelimiter from "components/ChooseDelimiter/ChooseDelimiter";
import { toast } from "sonner";
import { Loader } from "components/icons";

// query
import useResultQueryV2 from "pages/AdvancedSearchV2/queries";

// utils
import {
  extractEmailsFromBusinesses,
  formatUpdateAtDate,
  formatTimestamp,
} from "utils/helper";

// hooks
import { useCopyToClipboard } from "hooks/useCopyToClipboard";

// styles
import styles from "./EmailsExport.module.scss";

const EMAIL_EXPORT_LIMIT = 5000;

export default function EmailsExport() {
  const { copy } = useCopyToClipboard();

  const { data, isLoading, isError, dataUpdatedAt } = useResultQueryV2();

  const [delimiter, setDelimiter] = useState<string>(",");

  const results = data?.results;

  const showEmails = !isLoading && results && results?.length > 0 && !isError;
  const totalCount = results?.length?.toLocaleString("en-US") || 0;

  let emails = showEmails ? extractEmailsFromBusinesses(results) : [];

  const isOverLimit = emails?.length > EMAIL_EXPORT_LIMIT;

  if (isOverLimit) {
    emails = emails.slice(0, EMAIL_EXPORT_LIMIT);
  }

  const onCopy = useCallback(
    (text: string) => {
      if (text) {
        toast.success("Copied!", {
          position: "top-right",
        });

        copy(text);
      }
    },
    [copy]
  );

  const handleDelimiterOption = (value: string) => {
    setDelimiter(value === "comma" ? "," : ";");
  };

  return (
    <>
      <Helmet>
        <title>Emails Export | Small Business Search</title>
      </Helmet>

      <div className={styles["emails-export-root"]}>
        <Link
          to="/advanced"
          className={styles["back-link"]}
          aria-label="Back to advanced search page"
        >
          <Icon.ArrowBack size={3} aria-hidden="true" />
          <span>Back to results</span>
        </Link>

        <div className={styles["emails-export-content"]}>
          <h1 className={styles["title"]}>Export contact email addresses</h1>

          {showEmails && (
            <>
              <div className={styles["sentence-group"]}>
                <p>Small business</p> <AppliedFilterSentence />
              </div>

              <p className={styles["time-stamp"]}>
                {formatTimestamp(dataUpdatedAt)}
              </p>

              {!isOverLimit && (
                <p className={styles["desc"]}>
                  Listing {emails?.length?.toLocaleString("en-US")} email
                  addresses of {totalCount} businesses. Copy and paste the
                  following into your email client or distribution list. For a
                  complete list of email addresses from the results, export the
                  “Contacts” column from the result list instead.
                </p>
              )}

              {isOverLimit && (
                <p className={styles["desc"]}>
                  Listing 5,000 email addresses of {totalCount} businesses. Copy
                  and paste the following into your email client or distribution
                  list. The generated list is capped at a maximum of 5,000
                  emails due to email client constraints. For a complete list of
                  email addresses from the results, export the “Contacts” column
                  from the result list instead.
                </p>
              )}

              <hr className={styles["divider"]} />

              <div className={styles["copy-section"]}>
                <RoundedButton
                  type="button"
                  variant="ghost"
                  className={styles["copy-btn"]}
                  aria-label="Copy emails to clipboard"
                  onClick={() => onCopy(emails.join(`${delimiter} `))}
                >
                  <Icon.ContentCopy aria-hidden={true} />
                  Copy paragraph to clipboard
                </RoundedButton>

                <ChooseDelimiter
                  selectedOption={delimiter}
                  handleOption={handleDelimiterOption}
                />
              </div>

              {emails && emails.length > 0 && (
                <p className={styles["emails-content"]}>
                  {emails.join(`${delimiter} `)}
                </p>
              )}
            </>
          )}

          {!isLoading && !showEmails && (
            <div className={styles["no-result"]}>
              <p>No emails found</p>
              <Link to="/advanced" aria-label="Visit advanced search page">
                Try changing your filters
              </Link>
            </div>
          )}

          {isLoading && (
            <div className={styles["loader"]}>
              <Loader />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
